import ucskillsLogo from "assets/images/bob-ucskills-logo.svg";
import Footer from "features/Footer/Footer";
import WaitSpinner from "components/WaitSpinner";
import useFetch from "hooks/useFetch";
import useInput from "hooks/useInput";
import { useCallback, useEffect, useMemo, useState } from "react";
import LocalizedStrings from "react-localization";
import { useNavigate } from "react-router-dom";
import { MIN_PASSWORD_LENGTH, SEPARATOR } from "utils/constants";
import { getFetchOptions } from "utils/fetch";
import localizations from "./../../localization";
import "./Login.scss";

const Login = () => {
	const [waiting, setWaiting] = useState(true);

	window.sessionStorage.clear();
	window.indexedDB.deleteDatabase("ucskills-search");

	const strings = useMemo(() => new LocalizedStrings(localizations), []);
	strings.setLanguage("en_GB");

	const [efSetup, setEfSetup] = useState(false);
	const [email, onChangeEmail] = useInput("");
	const [emailValidation, setEmailValidation] = useState({ valid: 0, error: "" });
	const [passwordShown, setPasswordShown] = useState(false);
	const [password, onChangePassword] = useInput("");
	const [passwordValidation, setPasswordValidation] = useState({ valid: 0, error: "" });
	const [serverErrorMessage, setServerErrorMessage] = useState("");
	const navigate = useNavigate();
	const { execute } = useFetch();

	/* This code is a slight modification of the <script> code provided by heap: https://developers.heap.io/docs/web */
	const loadHeap = (heapId) => {
		if (!heapId) {
			console.warn("Heap ID is not available.");
			return;
		}

		window.heapReadyCb = window.heapReadyCb || [];
		window.heap = window.heap || [];

		window.heap.load = function (e, t) {
			window.heap.envId = e;
			window.heap.clientConfig = t || {};
			window.heap.clientConfig.shouldFetchServerConfig = false;
			
			const script = document.createElement("script");
			script.type = "text/javascript";
			script.async = true;
			script.src = `https://cdn.us.heap-api.com/config/${e}/heap_config.js`;

			const firstScript = document.getElementsByTagName("script")[0];
			firstScript.parentNode.insertBefore(script, firstScript);

			const methods = ["init", "startTracking", "stopTracking", "track", "resetIdentity", "identify", "getSessionId", "getUserId", "getIdentity", "addUserProperties",
				"addEventProperties", "removeEventProperty", "clearEventProperties", "addAccountProperties", "addAdapter", "addTransformer", "addTransformerFn", "onReady",
				"addPageviewProperties", "removePageviewProperty", "clearPageviewProperties", "trackPageview"];

			const createMethod = (method) => (...args) => {
				window.heapReadyCb.push({
					name: method,
					fn: () => window.heap[method] && window.heap[method](...args),
				});
			};
			methods.forEach((method) => {
				window.heap[method] = createMethod(method);
			});
		};
		window.heap.load(heapId);
	};

	/**
	 * Generate a "dummy" call to the Server for an initial Entity Framework request, for "warming-up".
	 */
	useEffect(() => {
		function onDatabaseCheckComplete() {
			setEfSetup(true);
			setWaiting(false);
			window.indexedDB.deleteDatabase("dummy");
		}

		fetch("/api/Setup/")
			.then(function (response) {
				sessionStorage.showSearch = true;
				let request = window.indexedDB.open("dummy", 1);
				request.addEventListener("success", () => {
					onDatabaseCheckComplete();
				});
				request.addEventListener("error", () => {
					if (navigator.userAgent.indexOf("Firefox/") !== -1) {
						sessionStorage.showSearch = false;
						onDatabaseCheckComplete();
					}
				});

				response.text().then(function (result) {
					if (result !== "false") {
						loadHeap(result);
					}
				});
			});
	}, []);

	const navigateToCorrectLocation = useCallback(() => {
		const userData = window.atob(sessionStorage.userData);
		const userDataArray = userData.split(SEPARATOR);
		let key = "ucs-lau-" + window.btoa(userDataArray[1]);
		let lau = localStorage.getItem(key);
		if (!lau) {
			key = "ucs-lau-anon";
			lau = localStorage.getItem(key);
		}
		if (lau) {
			navigate(lau, { replace: true });
			localStorage.removeItem(key);
		}
		else {
			navigate("/dashboard", { replace: true });
		}
	}, [navigate]);

	useEffect(() => {
		function onLoginRequestFailed(error) {
			if (("Status" in error) && (error.Status === 500)) {
				// Unhandled exception.
				let w = window.open("", "_blank");
				w.document.write(error.message);
			}
			else {
				let errorMessage = "";
				if (error.Message in strings) {
					errorMessage = strings[error.Message];
				}
				else if ("ExceptionMessage" in error) {
					errorMessage = [strings.ERROR, "<br />", error.ExceptionType, ": ", error.ExceptionMessage].join("");
				}
				else {
					errorMessage = error.Message;
				}
				setServerErrorMessage(errorMessage);
			}
		}

		if ((efSetup) && (emailValidation.valid === 2) && (passwordValidation.valid === 2) && (!serverErrorMessage.length)) {
			setWaiting(true);
			const options = getFetchOptions({
				method: "POST",
				auth: window.btoa(email + ":" + password)
			});
			execute("api/Login/", options, (content, error) => {
				setWaiting(false);
				if (error) {
					onLoginRequestFailed(error);
				}
				else {
					if (content.v3) {
						// Ensure the information required for ucSKILLS usage has been delivered.
						if ((!content.Authentication) || (!content.UserData)) {
							return;
						}
						sessionStorage.auth = content.Authentication;
						sessionStorage.hasReports = content.HasReports;
						sessionStorage.userData = content.UserData;
						sessionStorage.permissions = content.Permissions;
						sessionStorage.branding = content.Branding;
						sessionStorage.ff = content.FF;
						sessionStorage.nps = content.Nps;
						sessionStorage.gurus = JSON.stringify(content.Gurus);
						sessionStorage.firstLogin = content.FirstLogin;
						sessionStorage.showWelcome = content.ShowWelcome;
						sessionStorage.v3 = content.v3;
						sessionStorage.disableUserModification = content.DisableUserModification;
						sessionStorage.hasTeamsOffer = content.HasTeamsOffer;
						navigateToCorrectLocation();
					}
					else {
						localStorage.data = JSON.stringify(content);
						window.location.replace("site/main.html");
					}
				}
			});
		}
	}, [efSetup, email, emailValidation, password, passwordValidation, serverErrorMessage, strings, navigateToCorrectLocation]);

	function handleClickTogglePassword() {
		setPasswordShown(!passwordShown);
	}

	function handleClickSubmit() {
		setServerErrorMessage("");
		if (!email.trim().length) {
			setEmailValidation({ valid: 1, error: "ERR_USER_EMPTY" });
		}
		else {
			setEmailValidation({ valid: 2 });
		}
		if (!password.trim().length) {
			setPasswordValidation({ valid: 1, error: "ERR_PASSWORD_EMPTY" });
		}
		else if (password.trim().length < MIN_PASSWORD_LENGTH) {
			setPasswordValidation({ valid: 1, error: "ERR_PASSWORD_TOOSHORT" });
		}
		else {
			setPasswordValidation({ valid: 2 });
		}
	}

	function handleKeyUpEmail() {
		setEmailValidation({ valid: 0 });
	}

	function handleKeyUpPassword(event) {
		setPasswordValidation({ valid: 0 });
		if (event.keyCode === 13) {
			handleClickSubmit();
		}
	}

	return (
		<>
			<div id="container">
				<div className="login-content">
					{waiting && <WaitSpinner />}
					<div id="logo" className="centred">
						<img src={ucskillsLogo} alt="ucSKILLS Logo" width="150" height="89" />
					</div>
					<div id="content" className="centred">
						<div>
							<h1 id="title">{strings.LOGIN_TITLE}</h1>
						</div>
						<div id="email">
							<input
								id="email-value"
								className={(emailValidation.valid === 1) ? "is-invalid" : ""}
								maxLength="320"
								autoFocus="{true}"
								placeholder={strings.EMAIL_ADDRESS}
								value={email}
								onChange={onChangeEmail}
								onKeyUp={handleKeyUpEmail}
							/>
							<div className={`invalid-feedback ${emailValidation.valid === 0 ? "hidden" : ""}`}>{strings[emailValidation.error]}</div>
						</div>
						<div id="sesame">
							<input
								id="sesame-value"
								className={(passwordValidation.valid === 1) ? "is-invalid" : ""}
								type={passwordShown ? "text" : "password"}
								maxLength="64"
								placeholder={strings.PASSWORD}
								value={password}
								onChange={onChangePassword}
								onKeyUp={handleKeyUpPassword}
							/>
							<button
								id="sesame-reveal"
								className={passwordShown ? "unrevealed" : "revealed"}
								onClick={handleClickTogglePassword}
								title={passwordShown ? strings.CLICK_SHOW_PASSWORD : strings.CLICK_HIDE_PASSWORD}
							/>
							<div className={`invalid-feedback ${passwordValidation.valid === 0 ? "hidden" : ""}`}>{strings[passwordValidation.error]}</div>
						</div>
						<a id="sesame-forgot" href="/site/verify.html?T=1">{strings.FORGOT_PASSWORD}</a>
						<div id="errors" className="error-message">
							<span dangerouslySetInnerHTML={{ __html: serverErrorMessage }} />
						</div>
						<button id="submit" onClick={handleClickSubmit}>{strings.CLICK_LOGIN}</button>
					</div>
				</div>
				<Footer whitelabelled={false} loggedIn={ false } />
			</div>
		</>
	);
};

export default Login;